import { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertTriangle } from 'lucide-react';
import { usePOCancellation } from '@/hooks/usePOCancellation';

interface CancellationRequestDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  purchaseOrderId: string;
  poNumber: string;
  onSuccess?: () => void;
}

export const CancellationRequestDialog = ({
  open,
  onOpenChange,
  purchaseOrderId,
  poNumber,
  onSuccess,
}: CancellationRequestDialogProps) => {
  const [reason, setReason] = useState('');
  const { requestCancellation } = usePOCancellation();

  const handleSubmit = () => {
    if (reason.trim().length < 10) {
      return;
    }

    requestCancellation.mutate(
      { purchaseOrderId, reason },
      {
        onSuccess: () => {
          setReason('');
          onOpenChange(false);
          onSuccess?.();
        },
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Request Purchase Order Cancellation</DialogTitle>
          <DialogDescription>
            Request to cancel PO {poNumber}. The vendor will review your request and may propose a cancellation fee.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              Once a cancellation is approved and processed, it cannot be reversed. The vendor may charge a cancellation fee for work already completed or materials purchased.
            </AlertDescription>
          </Alert>

          <div className="space-y-2">
            <Label htmlFor="reason">Cancellation Reason *</Label>
            <Textarea
              id="reason"
              placeholder="Please provide a detailed reason for cancelling this purchase order (minimum 10 characters)..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={5}
              className="resize-none"
            />
            <p className="text-sm text-muted-foreground">
              {reason.length} / 10 characters minimum
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={requestCancellation.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleSubmit}
            disabled={reason.trim().length < 10 || requestCancellation.isPending}
          >
            {requestCancellation.isPending ? 'Submitting...' : 'Request Cancellation'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
